// ルールベースの簡易分類。docs/prototype.html の ruleClassify() を移植したもの。
// - functions/api/classify.ts から「LLM呼び出し失敗時のフォールバック」として使う
// - src/lib/ruleClassify.ts から再エクスポートし、フロントの即時プレビューにも使う
// AIより精度は落ちるが、キーワードマッチ+正規表現のみで完結しネットワーク依存が無い。
import type { ClassifiedFields } from "../../../src/types";

const HAS = (t: string, words: string[]) => words.some((w) => t.includes(w));

function isoToday(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ruleClassify(rawText: string): ClassifiedFields {
  const raw = (rawText || "").trim();
  const t = raw.replace(/\s+/g, " ");

  let category: ClassifiedFields["category"] = "一般連絡・お知らせ";
  let urgency: ClassifiedFields["urgency"] = "低";

  if (
    HAS(t, [
      "回収",
      "リコール",
      "自主回収",
      "欠品",
      "出荷停止",
      "副作用",
      "健康被害",
      "安全性",
      "ブルーレター",
      "イエローレター",
      "至急",
      "緊急",
    ])
  ) {
    category = "注意喚起・安全情報";
    urgency = "高";
  } else if (
    HAS(t, ["用法", "用量", "服薬", "効能", "添付文書", "相互作用", "一包化", "後発", "ジェネリック", "規格", "剤形"])
  ) {
    category = "服薬指導・薬品情報";
    urgency = "中";
  } else if (HAS(t, ["在庫", "発注", "納品", "入荷", "卸", "メーカー欠品", "限定出荷", "分割", "返品"])) {
    category = "在庫・発注連絡";
    urgency = "中";
  } else if (HAS(t, ["厚労省", "保険", "算定", "診療報酬", "改定", "通知", "届出", "制度", "加算", "レセプト"])) {
    category = "行政・制度情報";
    urgency = "中";
  }
  if (HAS(t, ["至急", "緊急", "本日中", "即日"])) urgency = "高";

  // 日付抽出
  let date = isoToday();
  const md = t.match(/(20\d{2})[/\-年.](\d{1,2})[/\-月.](\d{1,2})/);
  if (md) {
    date = `${md[1]}-${md[2].padStart(2, "0")}-${md[3].padStart(2, "0")}`;
  }

  // 文分割
  const clean = raw.replace(/【[^】]*】/g, " ");
  const sentences = clean
    .split(/[。\n!！?？]/)
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 4);

  const firstS = sentences[0] || "";
  const title = firstS.slice(0, 40) || "無題のお知らせ";
  const summary = firstS.length > 46 ? firstS.slice(0, 58) + "…" : firstS || "内容をご確認ください。";

  const actRe = /(ください|お願い|願います|対応|確認|停止|中止|返品|回収|交付|報告|届出|提出|周知|注意)/;
  const actions: string[] = [];
  const points: string[] = [];
  for (const s of sentences) {
    if (actRe.test(s) && actions.length < 6) {
      actions.push(s.replace(/^[、,\s]+/, "").slice(0, 60));
    } else if (points.length < 5 && s !== firstS) {
      points.push(s.slice(0, 60));
    }
  }
  if (!points.length && sentences.length > 1) points.push(sentences[1].slice(0, 60));

  // 対象
  let target = "";
  const tg = sentences.find((s) => /(錠|カプセル|散|液|軟膏|mg|ml|ロット|製剤|対象)/.test(s));
  if (tg) {
    const mm = tg.match(/([^、,]*(?:錠|カプセル|散|液|軟膏|製剤)[^、,]*(?:\d+\s*(?:mg|ml))?[^、,]*(?:ロット[^、,\s]*)?)/);
    target = (mm ? mm[1] : tg).slice(0, 50).trim();
  }

  // 期限
  let deadline = "";
  if (HAS(t, ["至急", "緊急", "即日"])) deadline = "至急";
  else if (t.includes("本日中")) deadline = "本日中";
  const dl = t.match(/(\d{1,2}\s*月\s*\d{1,2}\s*日|\d{1,2}\/\d{1,2})\s*まで/);
  if (dl) deadline = dl[1] + "まで";

  // 連絡先
  const ph = t.match(/(0\d{1,4}[-(]?\d{1,4}[-)]?\d{2,4})/);
  const win = t.match(/([^、。\s]{2,20}(?:お客様窓口|問い合わせ|窓口|コールセンター|担当)[^、。\s]{0,10})/);
  const contact = [win ? win[1] : "", ph ? ph[1] : ""].filter(Boolean).join(" ").trim();

  return { title, category, urgency, date, summary, points, actions, target, deadline, contact };
}
